import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  LinearProgress,
} from "@mui/material";
import { Button, Text, Card } from "@sede-enterprise/shell-ds-react-framework";
import { useTranslation } from "react-i18next";

export interface IdleTimeoutModalProps {
  isOpen: boolean;
  onExtendSession: () => void;
  onLogout: () => void;
  timeRemaining: number; // seconds until forced logout
}

/**
 * Modal component that warns users about idle timeout
 */
const COLOR_WARNING_MAIN = "var(--shell-color-warning-main, #f59e0b)";
const COLOR_TEXT_ON_PRIMARY = "var(--shell-color-text-on-primary, #ffffff)";
const COLOR_TEXT_SECONDARY = "var(--shell-color-text-secondary, #64748b)";
const COLOR_ERROR_MAIN = "var(--shell-color-error-main, #dc2626)";

export const IdleTimeoutModal: React.FC<IdleTimeoutModalProps> = ({
  isOpen,
  onExtendSession,
  onLogout,
  timeRemaining,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(timeRemaining);

  // Update countdown every second
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Automatically logout when countdown reaches 0
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, onLogout]);

  // Reset countdown when modal opens/time remaining changes
  useEffect(() => {
    setCountdown(timeRemaining);
  }, [timeRemaining, isOpen]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const progressValue = ((timeRemaining - countdown) / timeRemaining) * 100;
  const isUrgent = countdown <= 30; // Last 30 seconds is urgent

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "12px",
          border: `2px solid ${COLOR_WARNING_MAIN}`,
          boxShadow: "0 8px 32px rgba(245, 158, 11, 0.15)",
          backgroundColor: "var(--shell-color-background-primary, #ffffff)",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: COLOR_WARNING_MAIN,
          color: COLOR_TEXT_ON_PRIMARY,
          textAlign: "center",
          padding: "24px",
          margin: 0,
        }}
      >
        <Text
          size="large"
          style={{
            fontWeight: 600,
            fontSize: "1.5rem",
            margin: 0,
            color: COLOR_TEXT_ON_PRIMARY,
          }}
        >
          {t("session.timeout.title", "Session Timeout Warning")}
        </Text>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "32px 24px",
          textAlign: "center",
          backgroundColor: "var(--shell-color-background-primary, #ffffff)",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Text
            size="medium"
            style={{
              fontSize: "1.1rem",
              color: "var(--shell-color-text-primary, #1e293b)",
              marginBottom: "16px",
              lineHeight: 1.6,
              display: "block",
            }}
          >
            {t(
              "session.timeout.message",
              "Your session will expire due to inactivity.",
            )}
          </Text>

          <Text
            size="small"
            style={{
              color: COLOR_TEXT_SECONDARY,
              marginBottom: "24px",
              display: "block",
            }}
          >
            {t(
              "session.timeout.instruction",
              "Choose an option below to continue or you will be automatically logged out.",
            )}
          </Text>

          {/* Countdown Timer */}
          <Card
            style={{
              padding: "24px",
              backgroundColor: isUrgent
                ? "var(--shell-color-error-subtle, #fef2f2)"
                : "var(--shell-color-warning-subtle, #fefbf0)",
              borderRadius: "8px",
              border: `2px solid ${
                isUrgent ? COLOR_ERROR_MAIN : COLOR_WARNING_MAIN
              }`,
              marginBottom: "24px",
            }}
          >
            <Text
              size="large"
              style={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: isUrgent ? COLOR_ERROR_MAIN : COLOR_WARNING_MAIN,
                marginBottom: "8px",
                fontFamily: "monospace",
                display: "block",
                textAlign: "center",
              }}
            >
              {formatTime(countdown)}
            </Text>
            <Text
              size="small"
              style={{
                color: isUrgent
                  ? "var(--shell-color-error-dark, #b91c1c)"
                  : "var(--shell-color-warning-dark, #d97706)",
                textTransform: "uppercase",
                fontWeight: 600,
                letterSpacing: "0.5px",
                display: "block",
                textAlign: "center",
              }}
            >
              {isUrgent
                ? t("session.timeout.urgent", "Expiring Soon!")
                : t("session.timeout.remaining", "Time Remaining")}
            </Text>
          </Card>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor:
                  "var(--shell-color-background-subtle, #f8fafc)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: isUrgent
                    ? COLOR_ERROR_MAIN
                    : COLOR_WARNING_MAIN,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "0 24px 32px 24px",
          gap: 2,
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          onClick={onLogout}
          style={{
            minWidth: "120px",
            borderColor: COLOR_TEXT_SECONDARY,
            color: COLOR_TEXT_SECONDARY,
          }}
        >
          {t("session.timeout.logout", "Log Out Now")}
        </Button>

        <Button
          onClick={onExtendSession}
          style={{
            minWidth: "120px",
            backgroundColor: "var(--shell-color-primary-main, #007fa2)",
            color: COLOR_TEXT_ON_PRIMARY,
            fontWeight: 600,
          }}
        >
          {t("session.timeout.extend", "Stay Logged In")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
